import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Heart, Camera, CheckCircle, MessageCircle, Crown, Star, Globe, Award, Sparkles, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function CorporatePage() {
  return <LanguageProvider data-id="element-3630">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-3631">
        <ScrollProgress data-id="element-3632" />
        <Navbar data-id="element-3633" />
        <main className="w-full" data-id="element-3634">
          <CorporateHero data-id="element-3635" />
          <WhyPartnerSection data-id="element-3636" />
          <TheExperienceSection data-id="element-3637" />
          <SponsorshipTiersSection data-id="element-3638" />
          <AudienceSection data-id="element-3639" />
          <BenefitsSection data-id="element-3640" />
          <CorporateCTA data-id="element-3641" />
        </main>
        <Footer data-id="element-3642" />
      </div>
    </LanguageProvider>;
}
function CorporateHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-3643">
      <div className="absolute inset-0" data-id="element-3644">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-30" data-id="element-3645" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-3646"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-3647">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-3648">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-3649">
            <ArrowLeft className="w-4 h-4" data-id="element-3650" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-3651">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-3652">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-3653">
            Brand Partnership &amp; Sponsorship
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight leading-none" data-id="element-3654">
            CORPORATE
            <br data-id="element-3655" />
            <span className="text-amber-400" data-id="element-3656">PARTNERSHIP</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-3657"></div>
          <p className="text-xl md:text-2xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-3658">
            Transform Your Next Event Into an Unforgettable Cultural Experience
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-3659">
          <div className="text-center" data-id="element-3660">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-3661">
              Up to
            </p>
            <p className="font-serif text-3xl text-white" data-id="element-3662">120</p>
            <p className="text-xs font-light" data-id="element-3663">Guests Per Evening</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-3664"></div>
          <div className="text-center" data-id="element-3665">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-3666">
              Venue
            </p>
            <p className="font-serif text-xl text-white" data-id="element-3667">Illuminarium</p>
            <p className="text-xs font-light" data-id="element-3668">Toronto</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-3669"></div>
          <div className="text-center" data-id="element-3670">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-3671">
              Available
            </p>
            <p className="font-serif text-xl text-white" data-id="element-3672">Summer</p>
            <p className="text-xs font-light" data-id="element-3673">2026</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-3674">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-3675"></div>
      </motion.div>
    </section>;
}
function WhyPartnerSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-3676">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-3677"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-3678">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-3679">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-3680">
            The Case for Partnership
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-3681">
            WHY <span className="text-amber-400" data-id="element-3682">PARTNER</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-3683"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-3684">
            Life in the Sikh Kingdoms offers a rare opportunity to align your
            brand with heritage, excellence, and multicultural innovation at one
            of Toronto's most prestigious cultural events.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-3685">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-3686">
            <Building2 className="w-8 h-8 text-amber-400 mb-4" data-id="element-3687" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-3688">
              Brand Alignment
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-3689">
              Associate your brand with heritage, excellence, and multicultural
              innovation. Position yourself as a champion of cultural
              preservation and community investment in one of Canada's most
              dynamic cities.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-3690">
            <Crown className="w-8 h-8 text-amber-400 mb-4" data-id="element-3691" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-3692">
              Exclusive Access
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-3693">
              Host a private venue for up to 120 guests with the full immersive
              experience — 360° projections, royal Punjabi cuisine, and
              interactive technology. An evening your clients and partners will
              never forget.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-3694">
            <Heart className="w-8 h-8 text-amber-400 mb-4" data-id="element-3695" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-3696">
              Community Impact
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-3697">
              Align your CSR mandate with cultural preservation and youth
              education. A portion of all proceeds goes directly to the Sikh Art
              and Culture Gallery at the Royal Ontario Museum — a tangible,
              meaningful contribution.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-3698">
            <Camera className="w-8 h-8 text-amber-400 mb-4" data-id="element-3699" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-3700">
              Media Exposure
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-3701">
              Dignitary presence, press coverage, and premium photo
              opportunities. Events with cultural significance attract ethnic
              media, community papers, and broader press — amplifying your brand
              story organically.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function TheExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-3702">
      <div className="container mx-auto px-4" data-id="element-3703">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-3704">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-3705">
            What Your Guests Will Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-3706">
            THE <span className="text-amber-400" data-id="element-3707">EXPERIENCE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-3708"></div>
        </motion.div>

        <div className="max-w-6xl mx-auto" data-id="element-3709">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-3710">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-3711">
              <div className="aspect-video relative overflow-hidden" data-id="element-3712">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Immersive Experience" className="w-full h-full object-cover" data-id="element-3713" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-3714"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-3715">
              <p className="text-white/80 text-lg leading-relaxed font-light" data-id="element-3716">
                Step into the court of Maharaja Ranjit Singh, where history
                unfolds not behind glass, but all around you. Through walls that
                transform, floors that transport, and flavors that resurrect a
                golden age.
              </p>

              <div className="border-l-2 border-amber-400/30 pl-6 space-y-4" data-id="element-3717">
                <div className="flex items-start" data-id="element-3718">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-3719">—</span>
                  <span className="text-white/70 font-light" data-id="element-3720">
                    5–7 courses synchronized with immersive projections
                  </span>
                </div>
                <div className="flex items-start" data-id="element-3721">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-3722">—</span>
                  <span className="text-white/70 font-light" data-id="element-3723">
                    360° visual storytelling across walls, floor, and tables
                  </span>
                </div>
                <div className="flex items-start" data-id="element-3724">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-3725">—</span>
                  <span className="text-white/70 font-light" data-id="element-3726">
                    Royal Punjabi cuisine from world-renowned chefs
                  </span>
                </div>
                <div className="flex items-start" data-id="element-3727">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-3728">—</span>
                  <span className="text-white/70 font-light" data-id="element-3729">
                    90 minutes of complete immersion
                  </span>
                </div>
                <div className="flex items-start" data-id="element-3730">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-3731">—</span>
                  <span className="text-white/70 font-light" data-id="element-3732">
                    Interactive technology — voice, motion, and scent
                  </span>
                </div>
              </div>

              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-3733">
                <div className="grid grid-cols-3 gap-4 text-center" data-id="element-3734">
                  <div data-id="element-3735">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-3736">
                      Venue
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-3737">
                      Illuminarium Toronto
                    </p>
                  </div>
                  <div data-id="element-3738">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-3739">
                      Date
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-3740">Summer 2026</p>
                  </div>
                  <div data-id="element-3741">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-3742">
                      Capacity
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-3743">
                      Up to 120 Guests
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function SponsorshipTiersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-3744">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-3745"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-3746">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-3747">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-3748">
            Investment Levels
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-3749">
            SPONSORSHIP <span className="text-amber-400" data-id="element-3750">TIERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-3751"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-3752">
            Each tier is designed to deliver meaningful brand value while
            supporting cultural preservation and community impact.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3753">
          {/* Presenting Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0
        }} className="relative" data-id="element-3754">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-3755">
              <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2" data-id="element-3756">
                <Star className="w-4 h-4" fill="currentColor" data-id="element-3757" />
                <span className="text-xs uppercase tracking-wider" data-id="element-3758">
                  Flagship
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-12 h-full flex flex-col" data-id="element-3759">
              <div className="text-center mb-6" data-id="element-3760">
                <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-3761" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-3762">
                  Presenting Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-3763"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-3764">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3765">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3766"></span>
                  Full venue branding throughout the evening
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3767">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3768"></span>
                  2 Patron Circle tables (20 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3769">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3770"></span>
                  Logo on all event materials &amp; press releases
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3771">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3772"></span>
                  Press conference inclusion &amp; media opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3773">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3774"></span>
                  VIP reception hosting rights
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3775">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3776"></span>
                  Dedicated social media feature campaign
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3777">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3778"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Presenting%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-amber-400 hover:bg-amber-500 text-black py-3 text-center text-sm tracking-wider uppercase font-medium transition-colors duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-3779">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>

          {/* Cultural Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} data-id="element-3780">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-3781">
              <div className="text-center mb-6" data-id="element-3782">
                <Award className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-3783" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-3784">
                  Cultural Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-3785"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-3786">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3787">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3788"></span>
                  1 Patron Circle table (10 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3789">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3790"></span>
                  Logo on select event materials
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3791">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3792"></span>
                  Social media mention &amp; feature
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3793">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3794"></span>
                  Behind-the-scenes preview access
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3795">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3796"></span>
                  Branded photo moment at the event
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3797">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3798"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Cultural%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-3799">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>

          {/* Community Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-3800">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-3801">
              <div className="text-center mb-6" data-id="element-3802">
                <Globe className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-3803" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-3804">
                  Community Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-3805"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-3806">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3807">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3808"></span>
                  5 Founder Circle tickets
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3809">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3810"></span>
                  Logo on community-facing materials
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3811">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3812"></span>
                  Newsletter mention &amp; community recognition
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3813">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3814"></span>
                  Youth program sponsorship opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-3815">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-3816"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Community%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-3817">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function AudienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-3818">
      <div className="container mx-auto px-4" data-id="element-3819">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-3820">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-3821">
            Who You'll Reach
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-3822">
            AUDIENCE &amp; <span className="text-amber-400" data-id="element-3823">DEMOGRAPHICS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-3824"></div>
        </motion.div>

        <div className="max-w-5xl mx-auto" data-id="element-3825">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-id="element-3826">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-3827">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-3828">120</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-3829">
                Guests Per Evening
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-3830">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-3831">HNW</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-3832">
                High-Net-Worth Attendees
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-3833">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-3834">50+</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-3835">
                Diaspora Leaders &amp; Professionals
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-3836">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-3837">Press</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-3838">
                Media &amp; Cultural Influencers
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60 text-center" data-id="element-3839">
            <Heart className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-3840" />
            <p className="text-2xl text-amber-400 font-light italic mb-4" data-id="element-3841">
              "A portion of all proceeds goes directly to the Sikh Art and
              Culture Gallery at the Royal Ontario Museum, preserving Sikh
              heritage for future generations."
            </p>
            <p className="text-white/50 text-sm font-light tracking-wider uppercase" data-id="element-3842">
              Your sponsorship is a tax-deductible charitable contribution
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function BenefitsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-3843">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-3844"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-3845">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-3846">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-3847">
            Your Investment Includes
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-3848">
            WHAT SPONSORS <span className="text-amber-400" data-id="element-3849">RECEIVE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-3850"></div>
        </motion.div>

        <div className="max-w-4xl mx-auto" data-id="element-3851">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3852">
            {['Premium brand visibility throughout the event', 'Tax-deductible charitable donation receipt', 'Professional photography from the evening', 'Press kit inclusion and media distribution', 'Digital content package for social channels', 'Networking with community and diaspora leaders', 'Recognition in event program and credits', 'Access to future partnership opportunities'].map((benefit, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.05
          }} className="flex items-center gap-4 border border-amber-400/20 p-4 bg-black/40" data-id="element-3853">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-3854" />
                <span className="text-white/70 font-light text-sm" data-id="element-3855">
                  {benefit}
                </span>
              </motion.div>)}
          </div>
        </div>
      </div>
    </motion.section>;
}
function CorporateCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-3856">
      <div className="container mx-auto px-4" data-id="element-3857">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-3858">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-3859" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-3860">
            Ready to <span className="text-amber-400" data-id="element-3861">Partner?</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-3862"></div>
          <p className="text-xl text-white/70 font-light mb-10 leading-relaxed" data-id="element-3863">
            We welcome the opportunity to discuss how a partnership with Life in
            the Sikh Kingdoms can serve your brand, your team, and your
            community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3864">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20corporate%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-3865">
              <MessageCircle className="w-5 h-5" data-id="element-3866" />
              Inquire via WhatsApp
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-3867">
              <ArrowLeft className="w-4 h-4" data-id="element-3868" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-3869">
            Our team will respond within 24 hours to discuss partnership
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}